import { TAO_KOE, EN_KO, TaoConsole, Render, DynamicYieldAPI } from './../libs';

import RealtimeFilters from './realtime-filters.js';

import { getCurrentCurrency, getCurrencyCode } from './currency.js';

// Either JSX or lodash - jsx is cleaner code
// lodash renders faster ( because its only manipulating strings )
// For now leave both alternatives.
// import renderCarousel from './templates/productcarousel.tpl.html';
import renderCarousel from './templates/productcarousel_item.tpl.html';
// import templateFn from './templates/productcarousel.jsx';

// Add Observer, that checks the carousel for changes and reloads the carousel, if necessary.
const observableAttributes = [ 'data-widgetid', 'data-strategy', 'data-template', 'data-selector', 'data-action', 'data-maxresults', 'data-context', 'data-distinct', 'data-options', 'data-sizelink' ];
const observer = new MutationObserver( ( mutationList ) => {
	for ( const mutation of mutationList ) {
		if ( mutation.type === 'attributes' ) {
			const carouselEl = mutation.target;

			// Reset the Carousel.
			const instance = DYCarousel.getInstance( carouselEl );
			instance.resetCarousel();

			if ( DYCarousel.lazyload ) {
				// If the inView class does not exist, the default Lazyload Observer should still wait for the element to come into view.
				if ( carouselEl.classList.contains( 'tao_productcarousel--in_view' ) ) {
					carouselEl.classList.remove( 'tao_productcarousel--in_view' );
					EN_KO.functions.initSimpleLazyLoadOnScroll( carouselEl, 'tao_productcarousel--in_view', () => instance.render() );
				}
			} else {
				instance.render();
			}
		}
	}
} );

class DYCarousel {
	static lazyload = true;

	static Promises = [];

	constructor( el ) {
		this.carouselEl = el;

		// Setup necessary data.
		this.resetCarousel();

		// Reload the carousel after attributes changed.
		observer.observe( this.carouselEl, { attributes: true, attributeFilter: observableAttributes } );
	}

	resetCarousel() {
		this.strategy = this.carouselEl.dataset.strategy || this.carouselEl.dataset.widgetid;
		this.template = this.carouselEl.dataset.template || 'carousel';

		// Setup data sent to API.
		this.action = this.carouselEl.dataset.action || 'default';
		if ( 'options' === this.action ) {
			try {
				let parsed = this.carouselEl.dataset.options || '{}';
				if ( 'base64' === this.carouselEl.dataset.oencode ) {
					parsed = atob( parsed );
				}
				this.options = JSON.parse( parsed ) || {};
			} catch {
				this.options = {};
			}
		} else {
			this.options = {};
		}

		// Legacy Maxresults.
		if ( 'maxresults' in this.carouselEl.dataset ) {
			this.options.maxProducts = parseInt( this.carouselEl.dataset.maxresults || 0 );
		}

		// Legacy Context.
		if ( 'context' in this.carouselEl.dataset ) {
			this.options.customParam = this.carouselEl.dataset.context;
		}

		// Legacy Distinct.
		if ( 'distinct' in this.carouselEl.dataset ) {
			this.options.distinct = String( this.carouselEl.dataset.distinct || 'true' ) === 'true';
		}

		// Add Skeleton.
		if ( 'listing' !== this.template ) {
			// Save Skeleton for later use.
			const skeletonEl = this.carouselEl.querySelector( '.mdl_productskeleton' );
			if ( skeletonEl ) {
				this.skeletonTemplateEl = skeletonEl.cloneNode( true );
			} else if ( this.skeletonTemplateEl ) {
				const containerEl = this.getContainerEl();
				containerEl.replaceChildren( ...Array.from( Array( 12 ) ).map( () => this.skeletonTemplateEl.cloneNode( true ) ) );
				this.setLoading( true );
			}
		}

		// Clear responseData.
		this.responseData = null;
	}

	getData() {
		// Check for dynamic yield.
		const DY = window.DY;
		const DYO = window.DYO;

		// Return Cached Data.
		if ( this.responseData ) {
			return Promise.resolve( this.responseData );
		}

		if ( this.carouselEl.dataset.selector ) {
			const { selector, group } = this.carouselEl.dataset;
			const context = DY.recommendationContext || null;
			const opts = RealtimeFilters.getWidgetOpts( this.action, { context, ...this.options } );

			return DynamicYieldAPI.getChoices( selector, { group, context, ...opts } ).then( ( { choices } ) => {
				const choice = Object.values( choices ).find( ( { name } ) => name === selector );
				if ( choice ) {
					const variationData = choice.variations[ 0 ].payload.data;

					return {
						...variationData,
						slots: ( variationData.slots || [] ).map( ( { productData, ...slot } ) => {
							return {
								type: 'Campaign',
								item: {
									sku: slot.sku,
									...productData,
								},
								...slot,
							};
						} ),
					};
				}

				return Promise.reject( 'Invalid response!' );
			} ).then( ( data ) => {
				this.responseData = data;
				return data;
			} );
		}

		if ( ! DYO ) {
			return Promise.reject( 'Dynamic Yield not loaded!' );
		}

		return Promise.race( [
			// Load Data.
			new Promise( ( resolve, reject ) => {
				const opts = RealtimeFilters.getWidgetOpts( this.action, this.options );

				const success = DYO.recommendationWidgetData( this.strategy, opts, ( error, data ) => {
					if ( error ) {
						reject( error );
					} else {
						resolve( [ data, opts ] );
					}
				} );

				if ( ! success ) {
					reject( 'Invalid DYO Strategy ID: ' + this.strategy );
				}
			} ),
			// Cancel after 15 seconds.
			new Promise( ( resolve, reject ) => {
				setTimeout( () => reject( 'Timeout while waiting for response: ' + this.strategy ), 15000 );
			} ),
		] ).then( ( [ data, opts ] ) => {
			// Exclude items with more than one group-id, so that it exist only once except chanel group_id.
			if ( opts.distinct !== false ) {
				data.slots = data.slots.filter( ( value, index, self ) => {
					return self.findIndex( ( v ) => v.item.group_id === value.item.group_id ) === index;
				} );
			}

			// Slice down recommendationscarousel itemcount to nearest 3-dividable.
			// See https://business.acl.at/jira/browse/KOC-8341
			if ( 'recommendations' === this.template ) {
				data.slots = data.slots.slice( 0, Math.min( Math.floor( data.slots.length / 3 ) * 3, 6 ) );
			}

			// Cache Widget Data.
			this.responseData = data;
			return data;
		} );
	}

	getContainerEl() {
		return 'listing' === this.template ? this.carouselEl : this.carouselEl.querySelector( '.productcarousel__list_inner' );
	}

	render() {
		const containerEl = this.getContainerEl();

		const promise = this.getData()
			.then( async( data ) => {
				if ( this.lastPromise !== promise ) {
					// Do not resolve this promise, instead we wait for the latest promise to resolve instead.
					return new Promise( () => {} );
				}

				return [ data, await Promise.race( [ window.ACL_USER_DATA || [] ] ).catch( () => [] ) ];
			} )
			.then( ( [ data, userdata ] ) => {
				const items = data.slots.map( function( item ) {
					return new DYCarouselItem( item );
				} );

				if ( items.length <= 0 ) {
					throw 'No items returned for DYO: ' + ( this.carouselEl.dataset.selector || this.strategy );
				}

				// Update Title HTML.
				if ( data.custom && data.custom.title ) {
					const titleEl = this.carouselEl.querySelector( '.productcarousel__title' );
					if ( titleEl ) {
						titleEl.innerText = data.custom.title;
					}
				}

				const start = Date.now();
				return Promise.all( items.map( ( item, index ) => Render.render( 'productcarousel_item', {
					'element': item,
					index,
					userdata,
					'template': this.template || 'carousel',
					'target': this.carouselEl.dataset.linkTarget || false,
					'sizeLink': this.carouselEl.dataset.sizelink || false,
				} ) ) ).then( ( contents ) => {
					TaoConsole.log( 'RENDER', Date.now() - start );
					containerEl.innerHTML = contents.join( '' );
					TaoConsole.log( 'APPEND', Date.now() - start );

					if ( ! this.carouselEl.dataset.selector ) {
						// Setup Tracking Parameters.
						containerEl.setAttribute( 'data-dy-widget-id', data.wId );
						containerEl.setAttribute( 'data-dy-feed-id', data.fId );

						// Setup Trackings.
						setTimeout( () => DYCarousel.trackWithDy( containerEl ), 100 );
					}

					// Setup Datalayer push.
					setTimeout( () => DYCarousel.trackWithGTM( items ), 100 );

					return data;
				} );
			} ).then( ( data ) => {
				// Initialise Carousel.
				if ( 'listing' !== this.template ) {
					// Init carousel ( if needed ).
					this.carouselEl.dispatchEvent( new CustomEvent( 'carouselUpdateItems.sl', { detail: { data } } ) );
				}

				this.setLoading( false );
				this.carouselEl.dispatchEvent( new CustomEvent( 'simple_lazyload.sl', { bubbles: true } ) );

				return data;
			} ).catch( ( error ) => {
				// eslint-disable-next-line no-console
				console.error( error );

				// Hide Carousel.
				this.carouselEl.classList.add( 'en_hidden' );

				// Hide Section above if necessary.
				const sectionEl = this.carouselEl.closest( '.mdl_blocksection.mdl_blocksection--carousel' );
				if ( sectionEl ) {
					sectionEl.classList.add( 'en_hidden' );
				}

				return false;
			} );

		DYCarousel.Promises.push( promise );
		this.lastPromise = promise;
		return promise;
	}

	static trackWithGTM( items ) {
		const impressionList = items.map( function( item, index ) {
			return item.getTrackingData( index + 1 );
		} );

		if ( impressionList.length <= 0 ) {
			return;
		}

		// Limit number of impressions with each call ( there is a max of 8KB per event ).
		window.dataLayer = window.dataLayer || [];
		const chunkSize = 32;
		for ( let i = 0; i < impressionList.length; i += chunkSize ) {
			const chunk = impressionList.slice( i, i + chunkSize );
			window.dataLayer.push( {
				'event': 'productImpression',
				'ecommerce': {
					'currencyCode': getCurrentCurrency() || 'EUR',
					'impressions': chunk,
				},
			} );
		}
	}

	setLoading( isLoading = true ) {
		this.carouselEl.querySelectorAll( '.productcarousel__list' ).forEach( ( listEl ) => listEl.classList.toggle( 'is-loading', isLoading ) );
	}

	static trackWithDy( containerEl ) {
		const DYO = window.DYO;
		if ( DYO ) {
			DYO.recommendations.registerElements( containerEl );
		}
	}

	static getInstance( carouselEl ) {
		// Create new instance, if not exist.
		carouselEl.TAO_DYCarousel ??= new DYCarousel( carouselEl );
		return carouselEl.TAO_DYCarousel;
	}

	static renderInstance( carouselEl ) {
		const instance = DYCarousel.getInstance( carouselEl );
		return instance?.render();
	}
}

class DYCarouselItem {
	constructor( item ) {
		if ( 'Campaign' === item.type ) {
			this.type = 'Campaign';
			this.slotId = item.slotId;
		} else {
			this.type = 'Strategy';
			this.md = item.md;
			this.fallback = item.fallback;
			this.strId = item.strId;
		}

		this.data = this.convert( item );
	}

	convert( slot ) {
		// Get Item.
		const item = slot.item;

		item.deeplink = EN_KO.functions.make_link_relative( item.url );
		item.shortdescription = item.name;

		// Prices.
		item.oldpriceValue = this.convertToFloat( item.old_price );
		item.priceValue = this.convertToFloat( item.price );

		item.oldpriceFormat = this.formatPrice( item.old_price );
		item.priceFormat = this.formatPrice( item.price );

		// Images.
		item.image = item.image_url;
		item.hoverimage = item.image_url2;

		// Top Badges.
		item.new = 'true' === String( item.new );
		item.sale = 'true' === String( item.sale ) || ( item.oldpriceValue && item.oldpriceValue !== item.priceValue );
		item.onlineonly = 'true' === String( item.onlineonly );

		// Other Badges.
		item.badges = this.parseDYBadges( item );

		// Coming Soon.
		item.coming_soon = item.coming_soon && String( item.coming_soon ) !== 'false';

		// Get Currency Code
		item.currency_code = getCurrencyCode();

		if ( item.sale ) {
			item.salesPercentage = Math.abs( Math.round( ( item.priceValue - item.oldpriceValue ) / item.oldpriceValue * 100 ) );
		}

		return item;
	}

	formatPrice( price ) {
		const currency = getCurrentCurrency();

		const formatter = new Intl.NumberFormat( document.documentElement.lang, {
			style: 'currency',
			currency,
			currencyDisplay: 'code',
			minimumFractionDigits: 2,
		} );

		if ( 'function' === typeof formatter.formatToParts ) {
			return formatter.formatToParts( price ).filter( ( { type } ) => 'currency' !== type ).map( ( { value } ) => value ).join( '' ).trim();
		}

		return formatter.format( price ).replace( currency, '' ).trim();
	}

	convertToFloat( number ) {
		if ( 'number' === typeof number ) {
			return number;
		}

		if ( 'undefined' === typeof number ) {
			return '';
		}

		return parseFloat( number.toString().replace( ',', '.' ).replace( '€', '' ).replace( 'CHF', '' ).trim() );
	}

	parseDYBadges( item ) {
		if ( Array.isArray( item.badges ) ) {
			return item.badges;
		}

		const max = 3;
		const badgeMap = {
			'basics': 'Basics',
			'cyber_weekend': 'Basics',
			'exklusiv': 'Exklusiv',
			'experten_tipp': 'Experten Tipp',
			'fashion_tipp': 'Fashion Tipp',
			'gore_tex': 'Gore-Tex',
			'grosse_groessen': 'Grosse Grössen',
			'ispo_award': 'Ispo Award',
			'limited_edition': 'Limited Edition',
			'mix_match': 'Mix Match',
			'multi_pack': 'Multi Pack',
			'must_have': 'Best of',
			'preis_wert': 'Preiswert',
			'premium': 'Premium',
			'primaloft': 'Primaloft',
			'vibram': 'Vibram',
			'wear_fair': 'Nachhaltig',
			'xl_aermellaenge': 'XL Ärmellänge',
			'merino': 'Merino',
			'kaschmir': 'Kaschmir',
			'bestseller': 'Bestseller',
			'wasserfest': 'Wasserfest',
			'bestof': 'Best of',
			'weloveaustria': 'We ♡ Austria',
			'gigastyle': 'Gigastyle',
		};

		// Generate Badge Array.
		let badges = Object.keys( badgeMap ).map( ( key ) => {
			if ( 'true' !== String( item[ key ] ) ) {
				return false;
			}

			return {
				'name': key,
				'cssicon': 'en_badge--' + key,
				'title': badgeMap[ key ],
				'priority': 100,
			};
		} ).filter( ( badge ) => !! badge );

		badges.sort( function( a, b ) {
			if ( a.priority === b.priority ) {
				return 0;
			}

			return a.priority < b.priority ? -1 : 1;
		} );

		// Prepand Placeholders until we have 3 elements.
		badges = badges.slice( 0, max ); //badges.reverse().concat( Array( max ).fill( { 'placeholder': true } ) ).slice( 0, max ).reverse();

		return badges;
	}

	getTrackingData( index ) {
		// There is a typo there...
		const item = this.data;
		const category = item.categories[ item.categories.length - 1 ];

		return {
			'id': item.group_id,
			'name': item.name,
			'price': item.priceValue,
			'brand': item.brand,
			category,
			'list': document.location.pathname,
			'position': index || 0,
		};
	}

	getImage( imageUrl, width = 768, height = 1024, quality = 75 ) {
		let image = imageUrl;

		if ( width && height ) {
			image = image.replace( '-768_1024_75-', `-${ width }_${ height }_${ quality }-` );
		}

		return decodeURIComponent( image );
	}

	getImageForType( imageUrl, type = 'default' ) {
		if ( 'recommendations' === type || 'chanel' === type ) {
			return this.getImage( imageUrl, 330, 440 );
		}

		return this.getImage( imageUrl, 192, 256 );
	}

	escAttrOutput( itemAttr ) {
		return itemAttr.split( '&' ).join( '&amp;' ).split( '<' ).join( '&lt;' ).split( '"' ).join( '&quot;' ).split( "'" ).join( '&#39;' );
	}

	getSourceSet( imageUrl, type = 'default' ) {
		const image192 = this.getImage( imageUrl, 192, 256 );
		const image330 = this.getImage( imageUrl, 330, 440 );
		const image768 = this.getImage( imageUrl, 768, 1024 ); // eslint-disable-line no-unused-vars

		if ( 'recommendations' === type || 'chanel' === type ) {
			return [
				`${ image192 } 192w`,
				`${ image330 } 330w ,`,
			].join( ',' );
		}

		if ( 'crossselling' === type ) {
			return [
				`${ image192 } 192w`,
				`${ image192 } 330w`,
			].join( ',' );
		}

		return [
			`${ image192 } 192w`,
			`${ image192 } 330w`,
		].join( ',' );
	}

	isOnWishlist( userdata ) {
		const user = userdata?.data?.user;
		const wishlist = user.wishlist || [];

		const itemCount = wishlist.find( ( item ) => item.aid === this.data.sku )?.amt || 0;
		return itemCount > 0;
	}

	getWishlistLink( userdata ) {
		const user = userdata?.data?.user;

		if ( ! user?.loggedIn ) {
			return '/mein-konto/';
		}

		const params = new URLSearchParams( {
			'cl': 'account_noticelist',
			'fnc': 'addArticleToNoticeList',
			'aid': this.data.sku,
			'amt': this.isOnWishlist( userdata ) ? 1 : 0,
			'stoken': user.stoken,
		} );

		return '/index.php?' + params.toString();
	}

	addQueryParamsToUrl( url, params ) {
		// if URL is relative, we'll need to add a fake base
		const fakeBase = ! url.startsWith( 'http' ) ? 'http://fake-base.com' : undefined;
		const modifiedUrl = new URL( url || '', fakeBase );

		// add/update query params
		Object.keys( params ).forEach( function( key ) {
			if ( modifiedUrl.searchParams.has( key ) ) {
				modifiedUrl.searchParams.set( key, params[ key ] );
			} else {
				modifiedUrl.searchParams.append( key, params[ key ] );
			}
		} );

		// return as string (remove fake base if present)
		return modifiedUrl.toString().replace( fakeBase, '' );
	}
}

DYCarousel.item = DYCarouselItem;

// Initialise Carousel.
const initProductCarousel = ( scopeEl = document ) => {
	const carouselEls = scopeEl.querySelectorAll( '.tao_productcarousel[data-source="dy"]' );
	if ( carouselEls.length <= 0 ) {
		return;
	}

	// Load Carousels.
	if ( DYCarousel.lazyload ) {
		EN_KO.functions.initSimpleLazyLoadOnScroll( carouselEls, 'tao_productcarousel--in_view', ( carouselEl ) => DYCarousel.renderInstance( carouselEl ) );
	} else {
		carouselEls.forEach( ( carouselEl ) => DYCarousel.renderInstance( carouselEl ) );
	}

	const groups = Array.from( carouselEls ).filter( ( carouselEl ) => carouselEl.matches( '[data-selector][data-group]' ) ).reduce( ( list, carouselEl ) => {
		const group = carouselEl.dataset.group;
		if ( group ) {
			list[ group ] ??= [];
			list[ group ].push( carouselEl.dataset.selector );
		}
		return list;
	}, {} );
	const DY = window.DY;
	const context = DY?.recommendationContext || null;
	Object.entries( groups ).forEach( ( [ group, selectors ] ) => {
		DynamicYieldAPI.preload( selectors, { group, context } );
	} );
};

// Init.
TAO_KOE.ready( 'productcarousel-dy.js', () => {
	// Add Template to Render items.
	Render.addTemplate( 'productcarousel_item', renderCarousel );

	// Initialise Carousel.
	initProductCarousel();
	document.addEventListener( 'lazyload.tao', ( evt ) => initProductCarousel( evt.target || document ) );
	document.addEventListener( 'finished_change_variant', ( evt ) => initProductCarousel( evt.target || document ) );
} );

// Expose.
TAO_KOE.DYCarousel = DYCarousel;
export default DYCarousel;
